import comm from "csi/comm";
import {
	setFontFamilies,
	setFontWeights
} from "designSystem/fonts";
import reflection from "csi/reflection";
import {
	FontFamily,
	FontFileSrcMap,
	getVariableFontName,
	getVariableWeights,
	IFontInfoParsed,
	inlineFontRule,
} from "csi/utility/fontUtility";
import type { } from "css-font-loading-module";
import _ from "lodash";

/**
 * Get font style of currently selected span in DOM.
 */
export function getFroalaSelectionSpanStyle(style: string): string {
	// get the selected styles of the currently selected text
	const { anchorNode } = getSelection();
	let el = anchorNode as HTMLElement;
	let foundStyle = "";
	while (el && el.tagName !== "p") {
		const elStyle = el.style;
		if (elStyle?.[style]) {
			foundStyle = elStyle[style];
			break;
		}
		el = el.parentElement;
	}
	// remove single and double quotes from the returned style
	return foundStyle.replace(/['"]/g, "");
};

/**
 * Load fonts from files from reflection model (they were exported with ExportToReflectionModelCacheFile flag).
 */
export function loadFontsFromReflection(): void {
	const nodes = reflection.getReflectionInstancesByType("FontFamily");
	const fontFamilies = [];
	for (const node of nodes) {
		for (const file in node.FileInfoMap) {
			const info = JSON.parse(node.FileInfoMap[file]) as IFontInfoParsed;
			const {
				fontFamily,
				fvar,
				style,
				weight,
				widths,
			} = info;
			if (fvar) {
				// For variable fonts, do the variable blow out here, where each width adds another family.
				for (const width of widths) {
					const variableFamily = getVariableFontName(fontFamily, width);
					const fontFamilyStyle = variableFamily + style;
				
					// set state with family + style and the key as the weight words
					setFontWeights(fontFamilyStyle, getVariableWeights(weight));
					fontFamilies.push(variableFamily);
				}
			} else {
				const fontFamilyStyle = fontFamily + style;
				// Static fonts already have the width spelled out (if it's not regular).
				setFontWeights(fontFamilyStyle, weight);
				fontFamilies.push(fontFamily);
			}
		}
	}
	setFontFamilies(fontFamilies);
};

/**
 * Get font rules. Include style rules if requested.
 * Inline the rules (e.g., convert urls to base64) if requested.
 */
export async function getCSSRules(includeStyleRules?: boolean, inlineFontRules?: boolean, fontFamilies?: string[]): Promise<{}> {
	// Get stylesheets with href attached to process rules.
	const { styleSheets } = await document;
	const filteredSheets = [];
	for (const s in styleSheets) {
		const sheet = styleSheets[s];
		if (sheet.href) {
			filteredSheets.push(sheet);
		}
	}

	// Get style rules and font rules.
	const cssStyleRules = [];
	const cssFontFaceRules = [];
	for (const s in filteredSheets) {
		const sheet = filteredSheets[s];
		for (const r in sheet.cssRules) {
			const rule = sheet.cssRules[r];
			if (rule.type === CSSRule.FONT_FACE_RULE) {
				const { fontFamily } = rule.style;
				if (!fontFamilies || fontFamily === "Centric8" || fontFamilies.includes(fontFamily)) {
					if (inlineFontRules) {
						const baseUrl = rule.parentStyleSheet.href;
						const inline = await inlineFontRule(rule.cssText, baseUrl);
						cssFontFaceRules.push(inline);
					} else {
						cssFontFaceRules.push(rule.style);
					}
				}
			} else if (includeStyleRules && rule.type === CSSRule.STYLE_RULE) {
				cssStyleRules.push(rule.cssText);
			}
		}
	}

	return { cssStyleRules, cssFontFaceRules };
};

/**
 * Publish font file and add map to FontFileSrcMap
 */
export async function publishAndMapFont(fontFamily: FontFamily, file: File, fileSrcList: FontFileSrcMap): Promise<void> {
	const response = await comm.publishFile(file.name, null, file);
	fileSrcList[fontFamily] ||= {};
	fileSrcList[fontFamily][file.name] = response.getFileUrl();
};

/**
 * TODO: Build service to check that the fonts are loaded into the document
 * Check that fonts are loaded in document
 */
// export async function checkFontsLoaded(): Promise<void> {
// 	const { cssFontFaceRules } = await getCSSRules();
// 	for (const rule of cssFontFaceRules) {
// 		const fontFamily = rule.fontFamily.replace(/\"/g, "");
// 		const { fontStyle } = rule;
// 		const fontFamilyStyle = fontFamily + fontStyle;
// 		if (!CHECK_MAP[fontFamilyStyle]) {
// 			try {
// 				await new FontFaceObserver(fontFamily, {
// 					style: fontStyle,
// 				}).load();
// 				console.info("Successfully Loaded " + fontFamily + " " + fontStyle + " " + rule.fontWeight);
// 				CHECK_MAP[fontFamilyStyle] = true;
// 			} catch (err) {
// 				console.info("Warning: " + fontFamily + " didn't load.", err);
// 			}
// 		}
// 	}
// }
